import { useCallback, useEffect, useRef, useState } from 'react';
import { HiUpload, HiXCircle } from 'react-icons/hi';
import { HiArrowPath, HiDocument } from 'react-icons/hi2';
import { useModal } from '../../providers/useModal';

export type FileUploadStatus =
  | 'pending'
  | 'uploading'
  | 'paused'
  | 'completed'
  | 'error';

export interface UploadingFile {
  id: string;
  file: File;
  status: FileUploadStatus;
  progress: number;
  error?: string;
}

export interface LoadedFile {
  id: string;
  name: string;
  onRemove?: () => void;
}

export interface FileUploadProps {
  onFileSelect?: (files: File[]) => void;
  onFileRemove?: (fileId: string) => void;
  onFileResume?: (fileId: string) => void;
  accept?: string;
  maxSize?: number;
  multiple?: boolean;
  disabled?: boolean;
  uploadingFiles?: UploadingFile[];
  loadedFiles?: LoadedFile[];
}

function validateFile(
  file: File,
  options: { maxSize: number; accept: string }
): string | null {
  const { maxSize, accept } = options;
  const maxSizeBytes = maxSize * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return `Файл "${file.name}" превышает максимальный размер ${maxSize} МБ`;
  }
  if (accept && accept !== '*') {
    const acceptedExtensions = accept.split(',').map((ext) => ext.trim());
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!acceptedExtensions.includes(fileExtension)) {
      return `Файл "${file.name}" имеет недопустимый формат. Разрешенные форматы: ${accept}`;
    }
  }
  return null;
}

function formatTimeRemaining(seconds: number): string {
  if (seconds < 60) return `${seconds} секунд осталось`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  const word = minutes === 1 ? 'минута' : minutes < 5 ? 'минуты' : 'минут';
  return remainingSeconds === 0
    ? `${minutes} ${word} осталось`
    : `${minutes} ${word} ${remainingSeconds} секунд осталось`;
}

function getFileExtension(fileName: string): string {
  return fileName.split('.').pop()?.toLowerCase() || '';
}

function FileIcon({ fileName }: { fileName: string }) {
  const isPdf = getFileExtension(fileName) === 'pdf';
  return (
    <HiDocument
      className={`w-5 h-5 shrink-0 ${isPdf ? 'text-red-500' : 'text-gray-400'}`}
    />
  );
}

function ProgressCircle({
  progress,
  isPaused,
}: {
  progress: number;
  isPaused: boolean;
}) {
  const percentage = Math.min(100, Math.max(0, progress));
  const angle = (percentage / 100) * 360;
  const color = isPaused ? '#9ca3af' : '#1c64f2';
  const conicGradient = `conic-gradient(from -90deg, ${color} ${angle}deg, #e5e7eb ${angle}deg)`;
  return (
    <div className="relative w-8 h-8 shrink-0">
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background: conicGradient,
          mask: 'radial-gradient(circle at center, transparent 11px, black 13px)',
          WebkitMask:
            'radial-gradient(circle at center, transparent 11px, black 13px)',
        }}
      />
      {isPaused && (
        <div className="absolute inset-0 flex items-center justify-center">
          <HiArrowPath className="w-3 h-3 text-primary-600" />
        </div>
      )}
    </div>
  );
}

function CompletedFileItem({
  fileName,
  onRemove,
}: {
  fileName: string;
  onRemove?: () => void;
}) {
  return (
    <div className="flex items-center gap-3 flex-1 min-w-0">
      <FileIcon fileName={fileName} />
      <p className="text-sm font-medium text-gray-900 truncate flex-1 min-w-0">
        {fileName}
      </p>
      {onRemove && (
        <button
          onClick={onRemove}
          className="text-gray-400 hover:text-red-600 shrink-0"
          title="Удалить"
        >
          <HiXCircle className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

export const FileUpload: React.FC<FileUploadProps> = ({
  onFileSelect,
  onFileRemove,
  onFileResume,
  accept = '.pdf,.docx,.png,.jpg,.jpeg',
  maxSize = 10,
  multiple = true,
  disabled = false,
  uploadingFiles = [],
  loadedFiles = [],
}) => {
  const { openConfirmModal } = useModal();
  const [isDragging, setIsDragging] = useState(false);
  const [internalFiles, setInternalFiles] = useState<UploadingFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const displayFiles =
    uploadingFiles.length > 0 ? uploadingFiles : internalFiles;
  const isUncontrolled = uploadingFiles.length === 0;

  useEffect(() => {
    if (uploadingFiles.length > 0) setInternalFiles([]);
  }, [uploadingFiles.length]);

  useEffect(() => {
    if (isUncontrolled && onFileSelect && internalFiles.length > 0) {
      onFileSelect(internalFiles.map((u) => u.file));
    }
  }, [internalFiles, isUncontrolled, onFileSelect]);

  const handleFiles = useCallback(
    (files: File[]) => {
      const validFiles: File[] = [];
      const errors: string[] = [];
      const opts = { maxSize, accept };

      files.forEach((file) => {
        const error = validateFile(file, opts);
        if (error) errors.push(error);
        else validFiles.push(file);
      });

      if (errors.length > 0) {
        console.warn('Некоторые файлы не прошли валидацию:', errors);
      }

      if (validFiles.length === 0) return;

      if (uploadingFiles.length === 0) {
        setInternalFiles((prev) => [
          ...prev,
          ...validFiles.map((file, index) => ({
            id: `${Date.now()}-${index}`,
            file,
            status: 'pending' as const,
            progress: 0,
          })),
        ]);
      } else {
        onFileSelect?.(validFiles);
      }
    },
    [onFileSelect, uploadingFiles.length, maxSize, accept]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        handleFiles(Array.from(e.target.files));
        e.target.value = '';
      }
    },
    [handleFiles]
  );

  const handleRemove = useCallback(
    async (fileId: string) => {
      const confirmed = await openConfirmModal({
        title: 'Удалить документ?',
        content:
          'Вы всегда сможете добавить необходимые документы в любое время',
        okBtn: 'Подтвердить',
        cancelBtn: 'Отмена',
      });

      if (confirmed) {
        if (uploadingFiles.length > 0) {
          onFileRemove?.(fileId);
        } else {
          setInternalFiles((prev) => prev.filter((f) => f.id !== fileId));
        }
      }
    },
    [openConfirmModal, uploadingFiles.length, onFileRemove]
  );

  const dropZoneClass = [
    'h-full min-h-[140px] flex flex-col items-center justify-center p-8 bg-gray-50 border-2 border-dashed rounded-lg',
    isDragging ? 'border-primary-500 bg-primary-50' : 'border-gray-200',
    disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
  ].join(' ');

  const hintText =
    accept?.includes('.pdf') && accept.split(',').length === 1
      ? 'Перетащите сюда PDF файлы'
      : 'Перетащите сюда PDF, DOCX, PNG или JPEG файл.';

  const hasFilesToShow = displayFiles.length > 0 || loadedFiles.length > 0;

  return (
    <div className="w-full min-w-0">
      <div className="flex flex-col gap-6 min-w-0 md:flex-row justify-center">
        <div className="max-w-full grow">
          <label
            htmlFor="file-upload"
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!disabled) setIsDragging(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(false);
              if (!disabled && e.dataTransfer.files.length > 0) {
                handleFiles(Array.from(e.dataTransfer.files));
              }
            }}
            className={dropZoneClass}
          >
            <div className="flex flex-col items-center gap-2">
              <HiUpload className="w-5 h-5 text-gray-400" />
              <div className="text-sm font-semibold text-gray-500 text-center">
                <p>{hintText}</p>
                <p>Максимальный размер каждого файла {maxSize} МБ</p>
              </div>
              <p className="text-xs text-primary-600 underline">
                или выберите файл
              </p>
            </div>
            <input
              ref={fileInputRef}
              id="file-upload"
              type="file"
              className="hidden"
              accept={accept}
              multiple={multiple}
              onChange={handleFileInputChange}
              disabled={disabled}
            />
          </label>
        </div>

        {hasFilesToShow && (
          <div className="flex flex-col gap-4 w-full min-w-0 shrink-0 md:w-auto md:min-w-[200px] md:max-w-[320px]">
            {displayFiles.map((uf) =>
              uf.status === 'completed' ? (
                <CompletedFileItem
                  key={uf.id}
                  fileName={uf.file.name}
                  onRemove={() => handleRemove(uf.id)}
                />
              ) : (
                <div
                  key={uf.id}
                  className="flex gap-3 items-center p-2 rounded"
                >
                  <div className="flex gap-3 items-center flex-1 min-w-0">
                    {uf.status === 'uploading' || uf.status === 'paused' ? (
                      <ProgressCircle
                        progress={uf.progress}
                        isPaused={uf.status === 'paused'}
                      />
                    ) : (
                      <FileIcon fileName={uf.file.name} />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {uf.file.name}
                      </p>
                      {uf.status === 'uploading' && (
                        <div className="flex gap-2.5 items-center text-xs text-gray-500">
                          <span>{Math.round(uf.progress)}%</span>
                          {uf.progress > 0 && (
                            <span>
                              {formatTimeRemaining(
                                Math.round((100 - uf.progress) / 2)
                              )}
                            </span>
                          )}
                        </div>
                      )}
                      {uf.status === 'paused' && (
                        <div className="flex gap-2.5 items-center text-xs text-gray-500">
                          <span>{Math.round(uf.progress)}%</span>
                          <span>Загрузка отменена</span>
                        </div>
                      )}
                      {uf.status === 'error' && (
                        <p className="text-xs text-red-500">
                          {uf.error || 'Ошибка загрузки'}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-3 items-center shrink-0">
                    {uf.status === 'paused' && (
                      <button
                        type="button"
                        onClick={() => onFileResume?.(uf.id)}
                        className="text-primary-600 hover:text-primary-700"
                        title="Возобновить загрузку"
                      >
                        <HiArrowPath className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleRemove(uf.id)}
                      className="text-gray-400 hover:text-red-600"
                      title="Удалить"
                    >
                      <HiXCircle className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )
            )}

            {loadedFiles.map((loadedFile) => (
              <CompletedFileItem
                key={loadedFile.id}
                fileName={loadedFile.name}
                onRemove={loadedFile.onRemove}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
